import { useMemo } from 'react';
import {
  EnhancedGroupedSection,
  FormSchemaConfig,
} from '../../types/form/schema.types';
import { FormState } from '../../types';

// ============= Component =============
export const useDubaiTransferRequisitionSecondPartnerApprovalSchema = (
  formState: FormState
): FormSchemaConfig => {
  return useMemo<FormSchemaConfig>(() => {
    const isEditAllowed =
      formState.data?.userAssignedSections?.includes(
        'dubai_transfer_requisition_second_partner'
      ) === true;

    // Create the main section that matches EnhancedGroupedSection type
    const secondPartnerApprovalSection: EnhancedGroupedSection = {
      layout: 'single',
      isGrouped: true,
      isGroupedAccordion: false,
      sections: [
        {
          title: {
            text: 'Payment Authorisation - Second Partner Approval',
          },
          layout: 'single',
          fields: [
            {
              type: 'radio',
              name: 'secondPartnerApproval',
              label: 'Do you wish to authorise this payment?',
              options: [
                {
                  value: 'yes',
                  label: 'Yes',
                  disabled: false,
                },
                {
                  value: 'no',
                  label: 'No',
                  disabled: false,
                },
              ],
              required: true,
              validate: (value) =>
                !value
                  ? 'Please select whether you authorize this payment'
                  : undefined,
              disabled:
                formState.completedSections?.includes(
                  'dubai_transfer_requisition_second_partner'
                ) || !isEditAllowed,
            },
            {
              type: 'textArea',
              name: 'secondPartnerRejectionDetails',
              label: 'Rejection Details',
              required: formState.data.secondPartnerApproval === 'no',
              validate: (value) => {
                const isRequired = formState.data.secondPartnerApproval === 'no';
                if (!isRequired) return undefined;
                return !value
                  ? 'Please enter the rejection details'
                  : undefined;
              },
              disabled:
                formState.completedSections?.includes(
                  'dubai_transfer_requisition_second_partner'
                ) || !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'secondPartnerApproval',
                      operator: 'equals',
                      value: 'no',
                    },
                  ],
                },
              ],
            },
            {
              type: 'textArea',
              name: 'secondPartnerAuthApproveComments',
              label: 'Additional Comments',
              disabled:
                formState.completedSections?.includes(
                  'dubai_transfer_requisition_second_partner'
                ) || !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'secondPartnerApproval',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
      extras: { grouped: true, evenSpacing: true },
    };

    return {
      sections: [secondPartnerApprovalSection],
      dependencies: {
        showWhen: [
          {
            field: 'dubai_transfer_requisition_first_partner',
          },
        ],
      },
    };
  }, [formState]);
};
